import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Bell,
  Shield,
  User,
  Wallet,
  Code,
  Eye,
  EyeOff,
  Camera,
  Upload,
  ChevronDown,
  Loader,
  Copy,
  Check,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Globe,
} from "lucide-react";
import audioManager from "../utils/audioManager";
import { API_BASE_URL } from "../utils/apiConfig";
import usePermissions from "../hooks/usePermissions";

const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // ─── Tab Management ───
  const getInitialTab = () => {
    // Auto-select API tab when navigated from api-docs or api-credentials routes
    if (location.pathname.includes("api-docs") || location.pathname.includes("api-credentials")) {
      return "api";
    }
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam && ["profile", "security", "payment", "wallets", "api"].includes(tabParam)) {
      return tabParam;
    }
    return "profile";
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // ─── Profile State ───
  const [userProfile, setUserProfile] = useState({});
  const [editProfileData, setEditProfileData] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  // ─── Password State ───
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // ─── Audio Notification State ───
  const [audioStatus, setAudioStatus] = useState({
    enabled: false,
    canPlay: false,
  });

  // ─── Security / IP State ───
  const [ipAddresses, setIpAddresses] = useState([]);
  const [newIP, setNewIP] = useState("");
  const [ipLoading, setIpLoading] = useState(false);
  const [ipError, setIpError] = useState("");
  const [ipSuccess, setIpSuccess] = useState("");

  // ─── Payment Config State ───
  const [isEditingTimer, setIsEditingTimer] = useState(false);
  const [tempTimer, setTempTimer] = useState("");
  const [isEditingWithdrawAmount, setIsEditingWithdrawAmount] = useState(false);
  const [tempWithdrawAmount, setTempWithdrawAmount] = useState("");
  const [depositFeeDropdownOpen, setDepositFeeDropdownOpen] = useState(false);

  // ─── Static Wallets State ───
  const defaultStaticWallets = {
    isEnabled: false,
    erc20: { enabled: false, address: "", history: [] },
    trc20: { enabled: false, address: "", history: [] },
  };
  const [staticWallets, setStaticWallets] = useState(defaultStaticWallets);
  const [staticWalletLoading, setStaticWalletLoading] = useState(false);
  const [staticWalletError, setStaticWalletError] = useState("");
  const [staticWalletSuccess, setStaticWalletSuccess] = useState("");
  const [staticAwaitingCode, setStaticAwaitingCode] = useState(false);
  const [staticCode, setStaticCode] = useState("");
  const [staticCodeError, setStaticCodeError] = useState("");
  const [showHistory, setShowHistory] = useState({ erc20: false, trc20: false });

  // ─── Threshold State ───
  const [thresholdSettings, setThresholdSettings] = useState({
    enabled: false,
    amount: "",
    currency: "USDT",
  });
  const [thresholdLoading, setThresholdLoading] = useState(false);
  const [thresholdError, setThresholdError] = useState("");
  const [thresholdSuccess, setThresholdSuccess] = useState("");
  const [thresholdAwaitingCode, setThresholdAwaitingCode] = useState(false);
  const [thresholdCode, setThresholdCode] = useState("");
  const [thresholdCodeError, setThresholdCodeError] = useState("");

  // ─── API Credentials State (Tab 5) ───
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [apiTimer, setApiTimer] = useState("");
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [apiUserId, setApiUserId] = useState("");
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [webhookSecretFull, setWebhookSecretFull] = useState("");
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [webhookError, setWebhookError] = useState("");
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  // ─── Permissions ───
  const canViewApiCredentials = hasPermission("api-credentials");
  const canManageApiCredentials = hasPermission("api-credentials");

  // ─── Refs ───
  const depositFeeRef = useRef(null);

  // ══════════════════════════════════════════════════════════════
  //  HELPERS
  // ══════════════════════════════════════════════════════════════

  const normalizeStaticWallets = (data) => {
    if (!data) return defaultStaticWallets;
    if (data.staticWallets) {
      return {
        isEnabled: data.staticWallets.isEnabled || false,
        erc20: {
          enabled: data.staticWallets.erc20?.enabled || false,
          address: data.staticWallets.erc20?.address || "",
          history: data.staticWallets.erc20?.history || [],
        },
        trc20: {
          enabled: data.staticWallets.trc20?.enabled || false,
          address: data.staticWallets.trc20?.address || "",
          history: data.staticWallets.trc20?.history || [],
        },
      };
    }
    return defaultStaticWallets;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      const setters = {
        key: [setCopiedKey, 2000],
        secret: [setCopiedSecret, 2000],
        webhook: [setCopiedWebhook, 2000],
      };
      const [setter, ms] = setters[type] || [];
      if (setter) {
        setter(true);
        setTimeout(() => setter(false), ms);
      }
    } catch (err) {
      setApiError(`Failed to copy ${type}`);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  EFFECTS
  // ══════════════════════════════════════════════════════════════

  // Audio status polling
  useEffect(() => {
    const updateStatus = () => setAudioStatus(audioManager.getStatus());
    updateStatus();
    const interval = setInterval(updateStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  // Fetch profile on mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (depositFeeRef.current && !depositFeeRef.current.contains(event.target)) {
        setDepositFeeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-dismiss success messages
  useEffect(() => {
    if (profileSuccess) {
      const timer = setTimeout(() => setProfileSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [profileSuccess]);

  useEffect(() => {
    if (staticWalletSuccess) {
      const timer = setTimeout(() => setStaticWalletSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [staticWalletSuccess]);

  useEffect(() => {
    if (thresholdSuccess) {
      const timer = setTimeout(() => setThresholdSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [thresholdSuccess]);

  useEffect(() => {
    if (ipSuccess) {
      const timer = setTimeout(() => setIpSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [ipSuccess]);

  // Fetch API credentials when API tab is active
  useEffect(() => {
    if (activeTab === "api" && canViewApiCredentials) {
      fetchApiCredentials();
      fetchWebhookSecret();
    }
  }, [activeTab, canViewApiCredentials]);

  // ══════════════════════════════════════════════════════════════
  //  DATA FETCHING
  // ══════════════════════════════════════════════════════════════

  const fetchUserProfile = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setProfileError(t("settingsPage.errors.userNotLoggedIn"));
      setProfileLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/merchant/${userId}`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error(t("settingsPage.errors.failedToFetchProfile"));

      const data = await response.json();
      const merchant = data?.data || data?.merchant || data;

      setUserProfile(merchant);
      setEditProfileData({
        name: merchant.name || "",
        companyName: merchant.companyName || "",
        licenceNo: merchant.licenceNo || "",
        phoneNo: merchant.phoneNo || "",
        country: merchant.country || "",
      });

      const normalized = normalizeStaticWallets(merchant);
      setStaticWallets(normalized);

      if (merchant.thresholdWalletLimit) {
        setThresholdSettings({
          enabled: merchant.thresholdWalletLimit.enabled || false,
          amount: merchant.thresholdWalletLimit.amount || "",
          currency: merchant.thresholdWalletLimit.currency || "USDT",
        });
      }

      setTempTimer(merchant.transactionTimer?.toString() || "");
      setTempWithdrawAmount(merchant.maxAutoWithdrawAmount?.toString() || "100");

      fetchIPs();
    } catch (err) {
      setProfileError(t("settingsPage.errors.failedToLoadProfile"));
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchIPs = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/ip-whitelist`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Failed to fetch IPs");

      const data = await response.json();
      const ipList = data?.data || data?.ipAddresses || data || [];
      setIpAddresses(
        Array.isArray(ipList)
          ? ipList.filter((ip) => ip.status !== "deleted")
          : []
      );
    } catch (err) {
      setIpError(t("settingsPage.errors.failedToFetchIPs"));
    }
  };

  const fetchApiCredentials = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setApiError("User not logged in. Please login first.");
      setApiLoading(false);
      return;
    }
    setApiUserId(userId);

    try {
      const response = await fetch(
        `${API_BASE_URL}/merchant/${userId}/api-credentials`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to fetch API key: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) errorMessage = errorData.message;
        } catch (e) {}

        if (response.status === 403) throw new Error(`Access denied: ${errorMessage}`);
        if (response.status === 401) throw new Error(`Authentication failed. Please log in again.`);
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Find API key from response
      const foundKey =
        data?.apiKey || data?.data?.apiKey || data?.merchant?.apiKey || data?.api_key || data?.key || null;
      const secretIsHashed = !!(data?.data?.secretIsHashed || data?.secretIsHashed);
      const foundSecret = secretIsHashed
        ? null
        : (data?.apiSecret || data?.data?.apiSecret || data?.merchant?.apiSecret || data?.api_secret || data?.secret || null);

      if (foundKey) setApiKey(foundKey);
      if (foundSecret) setApiSecret(foundSecret);
      if (data?.data?.transactionTimer) setApiTimer(data.data.transactionTimer);

      if (!foundKey) {
        setApiError("API credentials not found in response.");
      } else {
        setApiError("");
      }
    } catch (err) {
      setApiError(err.message);
    } finally {
      setApiLoading(false);
    }
  };

  const fetchWebhookSecret = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/webhook/secret`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        if (response.status !== 401) setWebhookError("Failed to load webhook secret");
        return;
      }

      const data = await response.json();
      if (data.success) setWebhookSecret(data.webhookSecret);
    } catch (err) {
      setWebhookError("Failed to load webhook secret");
    } finally {
      setWebhookLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  HANDLERS — Profile
  // ══════════════════════════════════════════════════════════════

  const handleProfileInputChange = (field, value) => {
    setEditProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setProfileError(t("settingsPage.errors.selectImageFile"));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setProfileError("File size must be less than 5MB");
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveProfile = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setProfileLoading(true);
    setProfileError("");
    setProfileSuccess("");

    try {
      const formData = new FormData();
      formData.append("name", editProfileData.name || "");
      formData.append("companyName", editProfileData.companyName || "");
      formData.append("licenceNo", editProfileData.licenceNo || "");
      formData.append("phoneNo", editProfileData.phoneNo || "");
      formData.append("country", editProfileData.country || "");

      if (logoFile) {
        formData.append("logo", logoFile);
      }

      const response = await fetch(`${API_BASE_URL}/merchant/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || t("settingsPage.errors.failedToUpdateProfile"));
      }

      setProfileSuccess(t("settingsPage.errors.profileUpdatedSuccess"));
      setLogoFile(null);
      fetchUserProfile();
    } catch (err) {
      setProfileError(err.message || t("settingsPage.errors.failedToUpdateProfile"));
    } finally {
      setProfileLoading(false);
    }
  };

  // ──── Password ────

  const handlePasswordInputChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    setPasswordErrors((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const resetPasswordForm = () => {
    setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordErrors({});
    setPasswordSuccess(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!passwordData.currentPassword)
      errors.currentPassword = t("settingsPage.errors.currentPasswordRequired");
    if (!passwordData.newPassword)
      errors.newPassword = t("settingsPage.errors.newPasswordRequired");
    if (!passwordData.confirmPassword)
      errors.confirmPassword = t("settingsPage.errors.confirmPasswordRequired");
    if (passwordData.newPassword !== passwordData.confirmPassword)
      errors.confirmPassword = t("settingsPage.errors.passwordsDoNotMatch");
    if (passwordData.newPassword === passwordData.currentPassword)
      errors.newPassword = t("settingsPage.errors.newPasswordDifferent");

    if (passwordData.newPassword) {
      const pwd = passwordData.newPassword;
      if (pwd.length < 8 || !/[A-Z]/.test(pwd) || !/[a-z]/.test(pwd) || !/[0-9]/.test(pwd)) {
        errors.newPassword = t("settingsPage.profile.passwordRequirements");
      }
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = errData.message || t("settingsPage.errors.failedToChangePassword");

        if (msg.toLowerCase().includes("current password") || msg.toLowerCase().includes("incorrect")) {
          setPasswordErrors({ currentPassword: t("settingsPage.errors.currentPasswordIncorrect") });
        } else {
          setPasswordErrors({ general: msg });
        }
        return;
      }

      setPasswordSuccess(true);
      resetPasswordForm();
      setTimeout(() => {
        setPasswordSuccess(false);
        setShowChangePassword(false);
      }, 3000);
    } catch (err) {
      setPasswordErrors({ general: t("settingsPage.errors.failedToChangePassword") });
    } finally {
      setIsChangingPassword(false);
    }
  };

  // ──── Audio ────

  const handleEnableAudio = async () => {
    await audioManager.enableAudio();
    setAudioStatus(audioManager.getStatus());
  };

  const handleDisableAudio = () => {
    audioManager.disableAudio();
    setAudioStatus(audioManager.getStatus());
  };

  const handleResetPermissions = () => {
    audioManager.resetPermissions();
    setAudioStatus(audioManager.getStatus());
  };

  // ══════════════════════════════════════════════════════════════
  //  HANDLERS — Payment Config
  // ══════════════════════════════════════════════════════════════

  const handleAutoWithdrawToggle = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const newValue = !userProfile.autoWithdraw;

    try {
      const formData = new FormData();
      formData.append("autoWithdraw", newValue);

      const response = await fetch(`${API_BASE_URL}/merchant/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to update auto-withdraw");

      setUserProfile((prev) => ({ ...prev, autoWithdraw: newValue }));
      setProfileSuccess(`Auto-withdraw ${newValue ? "enabled" : "disabled"} successfully`);
    } catch (err) {
      setProfileError("Failed to update auto-withdraw setting");
    }
  };

  const handleSaveTimer = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const minutes = parseInt(tempTimer);
    if (isNaN(minutes) || minutes < 1 || minutes > 2880) {
      setProfileError("Timer must be between 1 and 2880 minutes");
      return;
    }

    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append("transactionTimer", minutes);

      const response = await fetch(`${API_BASE_URL}/merchant/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) throw new Error(t("settingsPage.errors.failedToUpdateTimer"));

      setUserProfile((prev) => ({ ...prev, transactionTimer: minutes }));
      setIsEditingTimer(false);
      setProfileSuccess("Transaction timer updated successfully");
    } catch (err) {
      setProfileError(t("settingsPage.errors.failedToUpdateTimer"));
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveWithdrawAmount = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const amount = parseFloat(tempWithdrawAmount);
    if (isNaN(amount) || amount < 0) {
      setProfileError("Amount must be a positive number");
      return;
    }

    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append("maxAutoWithdrawAmount", amount);

      const response = await fetch(`${API_BASE_URL}/merchant/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to update max auto-withdraw amount");

      setUserProfile((prev) => ({ ...prev, maxAutoWithdrawAmount: amount }));
      setIsEditingWithdrawAmount(false);
      setProfileSuccess("Max auto-withdraw amount updated successfully");
    } catch (err) {
      setProfileError("Failed to update max auto-withdraw amount");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleFeePayerUpdate = async (feeType, value) => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append(`${feeType}FeePayer`, value);

      const response = await fetch(`${API_BASE_URL}/merchant/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) throw new Error(`Failed to update ${feeType} fee payer`);

      setUserProfile((prev) => ({ ...prev, [`${feeType}FeePayer`]: value }));
      setProfileSuccess(`${feeType === "deposit" ? "Deposit" : "Withdrawal"} fee payer updated`);
    } catch (err) {
      setProfileError(`Failed to update ${feeType} fee payer`);
    } finally {
      setProfileLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  HANDLERS — Static Wallets
  // ══════════════════════════════════════════════════════════════

  const handleStaticWalletSave = async () => {
    setStaticWalletLoading(true);
    setStaticWalletError("");
    setStaticWalletSuccess("");

    try {
      const response = await fetch(`${API_BASE_URL}/merchant/static-wallet`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          isEnabled: staticWallets.isEnabled,
          erc20Enabled: staticWallets.erc20.enabled,
          erc20Address: staticWallets.erc20.address,
          trc20Enabled: staticWallets.trc20.enabled,
          trc20Address: staticWallets.trc20.address,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const codeNeeded =
          data?.data?.requires2FA ||
          data?.data?.requiresOTP ||
          data?.error === "2FA_CODE_REQUIRED" ||
          data?.error === "2FA_CODE_EXPIRED";
        if (codeNeeded) {
          setStaticAwaitingCode(true);
          return;
        }
        throw new Error(data.message || "Failed to save static wallets");
      }

      setStaticWalletSuccess(
        data.data?.autoTransferInitiated
          ? "Static wallets updated. Auto-transfer of available balance has been initiated."
          : "Static wallets updated successfully"
      );
      fetchUserProfile();
    } catch (err) {
      setStaticWalletError(err.message);
    } finally {
      setStaticWalletLoading(false);
    }
  };

  const handleSubmitStaticWallet = async () => {
    if (staticAwaitingCode) {
      if (!staticCode || staticCode.length !== 6) {
        setStaticCodeError("Please enter a valid 6-digit code");
        return;
      }

      setStaticWalletLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/merchant/static-wallet`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            isEnabled: staticWallets.isEnabled,
            erc20Enabled: staticWallets.erc20.enabled,
            erc20Address: staticWallets.erc20.address,
            trc20Enabled: staticWallets.trc20.enabled,
            trc20Address: staticWallets.trc20.address,
            twoFACode: staticCode,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Verification failed");

        setStaticWalletSuccess("Static wallets updated successfully");
        setStaticAwaitingCode(false);
        setStaticCode("");
        fetchUserProfile();
      } catch (err) {
        setStaticCodeError(err.message);
      } finally {
        setStaticWalletLoading(false);
      }
    } else {
      handleStaticWalletSave();
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  HANDLERS — Threshold
  // ══════════════════════════════════════════════════════════════

  const handleThresholdSave = async () => {
    setThresholdLoading(true);
    setThresholdError("");
    setThresholdSuccess("");

    try {
      const response = await fetch(`${API_BASE_URL}/merchant/static-wallet`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          thresholdEnabled: thresholdSettings.enabled,
          thresholdAmount: thresholdSettings.amount,
          thresholdCurrency: thresholdSettings.currency,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const codeNeeded =
          data?.data?.requires2FA ||
          data?.data?.requiresOTP ||
          data?.error === "2FA_CODE_REQUIRED" ||
          data?.error === "2FA_CODE_EXPIRED";
        if (codeNeeded) {
          setThresholdAwaitingCode(true);
          return;
        }
        throw new Error(data.message || "Failed to save threshold settings");
      }

      setThresholdSuccess("Threshold settings updated successfully");
      fetchUserProfile();
    } catch (err) {
      setThresholdError(err.message);
    } finally {
      setThresholdLoading(false);
    }
  };

  const handleSubmitThresholdSettings = async () => {
    if (thresholdAwaitingCode) {
      if (!thresholdCode || thresholdCode.length !== 6) {
        setThresholdCodeError("Please enter a valid 6-digit code");
        return;
      }

      setThresholdLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/merchant/static-wallet`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            thresholdEnabled: thresholdSettings.enabled,
            thresholdAmount: thresholdSettings.amount,
            thresholdCurrency: thresholdSettings.currency,
            twoFACode: thresholdCode,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Verification failed");

        setThresholdSuccess("Threshold settings updated successfully");
        setThresholdAwaitingCode(false);
        setThresholdCode("");
        fetchUserProfile();
      } catch (err) {
        setThresholdCodeError(err.message);
      } finally {
        setThresholdLoading(false);
      }
    } else {
      handleThresholdSave();
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  HANDLERS — IP Whitelist
  // ══════════════════════════════════════════════════════════════

  const handleAddIP = async () => {
    if (!newIP.trim()) {
      setIpError(t("settingsPage.errors.ipRequired"));
      return;
    }

    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(newIP.trim())) {
      setIpError(t("settingsPage.errors.invalidIPFormat"));
      return;
    }

    setIpLoading(true);
    setIpError("");

    try {
      const response = await fetch(`${API_BASE_URL}/ip-whitelist`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ipAddress: newIP.trim(), type: "server", status: "active" }),
      });

      if (!response.ok) throw new Error(t("settingsPage.errors.failedToAddIP", { type: "server" }));

      setNewIP("");
      setIpSuccess("IP address added successfully");
      fetchIPs();
    } catch (err) {
      setIpError(err.message);
    } finally {
      setIpLoading(false);
    }
  };

  const handleToggleIPStatus = async (ip) => {
    setIpLoading(true);
    try {
      const newStatus = ip.status === "active" ? "blocked" : "active";
      const response = await fetch(`${API_BASE_URL}/ip-whitelist/${ip._id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error(t("settingsPage.errors.failedToUpdateStatus"));

      fetchIPs();
    } catch (err) {
      setIpError(err.message);
    } finally {
      setIpLoading(false);
    }
  };

  const handleRemoveIP = async (ip) => {
    setIpLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ip-whitelist/${ip._id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "deleted" }),
      });

      if (!response.ok) throw new Error(t("settingsPage.errors.failedToRemoveIP", { type: "server" }));

      fetchIPs();
    } catch (err) {
      setIpError(err.message);
    } finally {
      setIpLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  HANDLERS — Webhook
  // ══════════════════════════════════════════════════════════════

  const handleRotateWebhookSecret = async () => {
    setRotateLoading(true);
    setWebhookError("");
    try {
      const response = await fetch(`${API_BASE_URL}/webhook/secret/rotate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to rotate webhook secret");
      }

      const data = await response.json();
      if (data.success) {
        setWebhookSecretFull(data.webhookSecret);
        setWebhookSecret(data.webhookSecret);
        setShowWebhookSecret(true);
        setShowRotateConfirm(false);
      }
    } catch (err) {
      setWebhookError(err.message || "Failed to rotate webhook secret");
    } finally {
      setRotateLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  TAB DEFINITIONS
  // ══════════════════════════════════════════════════════════════

  const tabs = [
    {
      id: "profile",
      name: t("settingsPage.tabs.profile.name"),
      description: t("settingsPage.tabs.profile.description"),
      icon: User,
    },
    {
      id: "security",
      name: t("settingsPage.tabs.security.name"),
      description: t("settingsPage.tabs.security.description"),
      icon: Shield,
    },
    {
      id: "payment",
      name: t("settingsPage.tabs.payment.name"),
      description: t("settingsPage.tabs.payment.description"),
      icon: Globe,
    },
    {
      id: "wallets",
      name: t("settingsPage.tabs.wallets.name"),
      description: t("settingsPage.tabs.wallets.description"),
      icon: Wallet,
    },
    {
      id: "api",
      name: t("settingsPage.tabs.api.name"),
      description: t("settingsPage.tabs.api.description"),
      icon: Code,
    },
  ];

  // ══════════════════════════════════════════════════════════════
  //  TAB 1: PROFILE
  // ══════════════════════════════════════════════════════════════

  const renderProfileTab = () => (
    <div className="space-y-6 md:space-y-8">
      {/* Company Info */}
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
          {t("settingsPage.profile.title")}
        </h2>
        <p className="text-sm text-gray-600 mb-6 md:mb-8">
          {t("settingsPage.profile.subtitle")}
        </p>

        {profileLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">{t("settingsPage.profile.loadingProfile")}</span>
          </div>
        )}

        {profileError && (
          <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{profileError}</p>
          </div>
        )}

        {profileSuccess && (
          <div className="mb-4 p-3 md:p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{profileSuccess}</p>
          </div>
        )}

        {!profileLoading && (
          <div className="space-y-4 md:space-y-6">
            {/* Logo Section */}
            <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F6F7F8] border border-gray-200 flex items-center justify-center">
                    {logoPreview || userProfile.logo ? (
                      <img
                        src={logoPreview || userProfile.logo}
                        alt="Company Logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  <label htmlFor="logo-upload" className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                    <Camera className="w-4 h-4 text-white" />
                    <input type="file" id="logo-upload" accept="image/*" onChange={handleLogoFileChange} className="hidden" />
                  </label>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">{t("settingsPage.profile.companyLogo")}</h3>
                <p className="text-sm text-gray-600 mb-3">{t("settingsPage.profile.logoDesc")}</p>
                <label htmlFor="logo-upload-button" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
                  <Upload className="w-4 h-4 mr-2" />
                  {t("settingsPage.profile.chooseFile")}
                  <input type="file" id="logo-upload-button" accept="image/*" onChange={handleLogoFileChange} className="hidden" />
                </label>
                {logoFile && <span className="ml-3 text-sm text-gray-600">{logoFile.name}</span>}
              </div>
            </div>

            {/* Profile Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("settingsPage.profile.fullName")}</label>
                <input type="text" value={editProfileData.name || ""} onChange={(e) => handleProfileInputChange("name", e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-0 focus:border-gray-400" placeholder={t("settingsPage.profile.enterFullName")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("settingsPage.profile.email")}</label>
                <input type="text" value={userProfile.email || ""} disabled className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-white text-gray-500 text-sm cursor-not-allowed" />
                <p className="text-xs text-gray-500 mt-1">{t("settingsPage.profile.emailCannotChange")}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("settingsPage.profile.companyName")}</label>
                <input type="text" value={editProfileData.companyName || ""} onChange={(e) => handleProfileInputChange("companyName", e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-0 focus:border-gray-400" placeholder={t("settingsPage.profile.enterCompanyName")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("settingsPage.profile.licenseNumber")}</label>
                <input type="text" value={editProfileData.licenceNo || ""} onChange={(e) => handleProfileInputChange("licenceNo", e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-0 focus:border-gray-400" placeholder={t("settingsPage.profile.enterLicenseNumber")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("settingsPage.profile.phoneNumber")}</label>
                <input type="tel" value={editProfileData.phoneNo || ""} onChange={(e) => handleProfileInputChange("phoneNo", e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-0 focus:border-gray-400" placeholder={t("settingsPage.profile.enterPhoneNumber")} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("settingsPage.profile.country")}</label>
                <input type="text" value={editProfileData.country || ""} onChange={(e) => handleProfileInputChange("country", e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-0 focus:border-gray-400" placeholder={t("settingsPage.profile.enterCountryName")} />
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <button onClick={handleSaveProfile} disabled={profileLoading} className="px-6 py-2.5 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-sm" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
                {profileLoading ? t("settingsPage.security.saving") : t("settingsPage.profile.saveChanges")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-[#F6F7F8] rounded-xl p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 mb-1">{t("settingsPage.profile.changePassword")}</h3>
            <p className="text-sm text-gray-600">{t("settingsPage.profile.changePasswordDesc")}</p>
          </div>
          <button onClick={() => setShowChangePassword(!showChangePassword)} className="px-6 py-2.5 text-white rounded-xl hover:opacity-90 transition-opacity text-sm font-medium whitespace-nowrap shadow-sm w-full sm:w-auto" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
            {showChangePassword ? t("settingsPage.profile.cancel") : t("settingsPage.profile.changePassword")}
          </button>
        </div>

        {showChangePassword && (
          <div className="mt-6 pt-6 border-t border-gray-300">
            {passwordSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">{t("settingsPage.profile.passwordChangedSuccess")}</p>
              </div>
            )}
            {passwordErrors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{passwordErrors.general}</p>
              </div>
            )}
            <form onSubmit={handleChangePassword} className="space-y-4">
              {["current", "new", "confirm"].map((field) => {
                const fieldKey = field === "current" ? "currentPassword" : field === "new" ? "newPassword" : "confirmPassword";
                const labelKey = field === "current" ? "currentPassword" : field === "new" ? "newPassword" : "confirmNewPassword";
                const placeholderKey = field === "confirm" ? "confirmNewPasswordPlaceholder" : field === "current" ? "enterCurrentPassword" : "enterNewPassword";
                return (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t(`settingsPage.profile.${labelKey}`)}</label>
                    <div className="relative">
                      <input
                        type={showPasswords[field] ? "text" : "password"}
                        value={passwordData[fieldKey]}
                        onChange={(e) => handlePasswordInputChange(fieldKey, e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${passwordErrors[fieldKey] ? "border-red-300" : "border-gray-300"}`}
                        placeholder={t(`settingsPage.profile.${placeholderKey}`)}
                      />
                      <button type="button" onClick={() => togglePasswordVisibility(field)} className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700">
                        {showPasswords[field] ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </div>
                    {passwordErrors[fieldKey] && <p className="mt-1 text-sm text-red-600">{passwordErrors[fieldKey]}</p>}
                    {field === "new" && <p className="mt-1 text-xs text-gray-500">{t("settingsPage.profile.passwordRequirements")}</p>}
                  </div>
                );
              })}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={isChangingPassword} className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)", borderRadius: "10px" }}>
                  {isChangingPassword ? t("settingsPage.profile.changingPassword") : t("settingsPage.profile.changePassword")}
                </button>
                <button type="button" onClick={resetPasswordForm} disabled={isChangingPassword} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {t("settingsPage.profile.reset")}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Audio Notifications */}
      <div className="bg-[#F6F7F8] rounded-xl p-4 md:p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{t("settingsPage.notifications.title")}</h3>
        <p className="text-sm text-gray-600 mb-4">{t("settingsPage.notifications.subtitle")}</p>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{t("settingsPage.notifications.status")}:</span>
            <span className={`text-sm font-semibold ${audioStatus.enabled ? "text-green-600" : "text-gray-500"}`}>
              {audioStatus.enabled ? t("settingsPage.notifications.enabled") : t("settingsPage.status.disabled")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{t("settingsPage.notifications.canPlay")}:</span>
            <span className={`text-sm font-semibold ${audioStatus.canPlay ? "text-green-600" : "text-gray-500"}`}>
              {audioStatus.canPlay ? t("settingsPage.notifications.yes") : t("settingsPage.notifications.no")}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {audioStatus.enabled ? (
            <button onClick={handleDisableAudio} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium">
              {t("settingsPage.notifications.disableAudio")}
            </button>
          ) : (
            <button onClick={handleEnableAudio} className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
              {t("settingsPage.notifications.enableAudio")}
            </button>
          )}
          <button onClick={handleResetPermissions} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium">
            {t("settingsPage.notifications.resetPermissions")}
          </button>
          <button onClick={() => audioManager.playTestSound()} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium">
            {t("settingsPage.notifications.testAudio")}
          </button>
        </div>

        <div className="mt-4 p-3 bg-white rounded-lg">
          <h4 className="text-sm font-medium text-gray-800 mb-2">{t("settingsPage.notifications.howItWorks")}</h4>
          <ul className="text-xs text-gray-600 space-y-1.5 list-disc list-inside">
            <li>{t("settingsPage.notifications.bullet1")}</li>
            <li>{t("settingsPage.notifications.bullet2")}</li>
            <li>{t("settingsPage.notifications.bullet3")}</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  //  TAB 2: SECURITY
  // ══════════════════════════════════════════════════════════════

  const renderSecurityTab = () => (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
          {t("settingsPage.tabs.security.name")}
        </h2>
        <p className="text-sm text-gray-600 mb-6">{t("settingsPage.tabs.security.description")}</p>
      </div>

      {/* 2FA */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{t("settingsPage.security.twoFactorAuth")}</h3>
            <p className="text-sm text-gray-600">{t("settingsPage.security.addExtraSecurity")}</p>
          </div>
          <button onClick={() => navigate("/security/2fa")} className="px-6 py-2.5 text-white rounded-xl hover:opacity-90 transition-opacity text-sm font-medium whitespace-nowrap shadow-sm w-full sm:w-auto" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
            {t("settingsPage.security.configure2FA")}
          </button>
        </div>
      </div>

      {/* IP Restriction */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px] space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">{t("settingsPage.ipRestriction.title")}</h3>
          <p className="text-sm text-gray-600">{t("settingsPage.ipRestriction.subtitle")}</p>
        </div>

        {ipError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{ipError}</p>
          </div>
        )}
        {ipSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{ipSuccess}</p>
          </div>
        )}

        {/* Add IP Form */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={newIP}
            onChange={(e) => { setNewIP(e.target.value); setIpError(""); }}
            placeholder={t("settingsPage.ipRestriction.enterServerIP")}
            className="flex-1 px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            disabled={ipLoading}
          />
          <button onClick={handleAddIP} disabled={ipLoading} className="px-5 py-2.5 text-white rounded-lg hover:opacity-90 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
            {t("settingsPage.ipRestriction.addServerIP")}
          </button>
        </div>

        {/* IP Table */}
        {ipAddresses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">{t("settingsPage.ipRestriction.srNo")}</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">{t("settingsPage.ipRestriction.ipAddress")}</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">{t("settingsPage.ipRestriction.status")}</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">{t("settingsPage.ipRestriction.action")}</th>
                </tr>
              </thead>
              <tbody>
                {ipAddresses.map((ip, index) => (
                  <tr key={ip._id || index} className="border-b border-gray-100">
                    <td className="py-2.5 px-3 text-gray-700">{index + 1}</td>
                    <td className="py-2.5 px-3 font-mono text-gray-900">{ip.ipAddress}</td>
                    <td className="py-2.5 px-3">
                      <button onClick={() => handleToggleIPStatus(ip)} disabled={ipLoading} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer disabled:cursor-not-allowed ${ip.status === "active" ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}>
                        {ip.status === "active" ? t("settingsPage.ipRestriction.active") : t("settingsPage.ipRestriction.blocked")}
                      </button>
                    </td>
                    <td className="py-2.5 px-3">
                      <button onClick={() => handleRemoveIP(ip)} disabled={ipLoading} className="text-red-600 hover:text-red-800 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {t("settingsPage.ipRestriction.remove")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <p className="text-sm font-medium">{t("settingsPage.ipRestriction.noServerIPs")}</p>
            <p className="text-xs mt-1">{t("settingsPage.ipRestriction.addServerIPDesc")}</p>
          </div>
        )}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  //  TAB 3: PAYMENT CONFIG
  // ══════════════════════════════════════════════════════════════

  const renderPaymentConfigTab = () => (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
          {t("settingsPage.tabs.payment.name")}
        </h2>
        <p className="text-sm text-gray-600 mb-6">{t("settingsPage.tabs.payment.description")}</p>
      </div>

      {profileError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{profileError}</p>
        </div>
      )}
      {profileSuccess && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-600 text-sm">{profileSuccess}</p>
        </div>
      )}

      {/* Auto-Withdraw */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px] space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 text-base sm:text-lg">{t("settingsPage.security.autoWithdraw")}</h3>
            <p className="text-sm text-gray-600">{t("settingsPage.security.autoWithdrawDesc")}</p>
          </div>
          <button onClick={handleAutoWithdrawToggle} disabled={profileLoading} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${userProfile.autoWithdraw ? "bg-blue-600" : "bg-gray-200"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userProfile.autoWithdraw ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        {/* Max Auto-Withdraw Amount */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">{t("settingsPage.security.maxAutoWithdrawAmount")}</label>
          <p className="text-xs text-gray-500 mb-2">{t("settingsPage.security.maxAutoWithdrawDesc")}</p>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <input
                type="number"
                value={isEditingWithdrawAmount ? tempWithdrawAmount : (userProfile.maxAutoWithdrawAmount || 100)}
                onChange={(e) => { setTempWithdrawAmount(e.target.value); setIsEditingWithdrawAmount(true); }}
                onFocus={() => { setTempWithdrawAmount((userProfile.maxAutoWithdrawAmount || 100).toString()); setIsEditingWithdrawAmount(true); }}
                className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-16"
                placeholder={t("settingsPage.security.enterMaxAmount")}
                min="0"
                step="0.01"
                disabled={profileLoading}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">{t("settingsPage.security.usdt")}</span>
            </div>
            {isEditingWithdrawAmount && (
              <button onClick={handleSaveWithdrawAmount} disabled={profileLoading} className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
                {profileLoading ? t("settingsPage.security.saving") : t("settingsPage.security.save")}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">{t("settingsPage.security.currentLimit", { amount: userProfile.maxAutoWithdrawAmount || 100 })}</p>
        </div>
      </div>

      {/* Transaction Timer */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px] space-y-3">
        <h3 className="font-medium text-gray-900 text-base sm:text-lg">{t("settingsPage.security.transactionTimer")}</h3>
        <p className="text-sm text-gray-600">{t("settingsPage.security.transactionTimerDesc")}</p>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <input
              type="number"
              value={isEditingTimer ? tempTimer : (userProfile.transactionTimer || "")}
              onChange={(e) => { setTempTimer(e.target.value); setIsEditingTimer(true); }}
              onFocus={() => { setTempTimer((userProfile.transactionTimer || "").toString()); setIsEditingTimer(true); }}
              className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-20"
              placeholder={t("settingsPage.security.enterTimerMinutes")}
              min="1"
              max="2880"
              disabled={profileLoading}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">{t("settingsPage.security.minutes")}</span>
          </div>
          {isEditingTimer && (
            <button onClick={handleSaveTimer} disabled={profileLoading} className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50" style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}>
              {profileLoading ? t("settingsPage.security.saving") : t("settingsPage.security.save")}
            </button>
          )}
        </div>
        {userProfile.transactionTimer && (
          <p className="text-xs text-gray-500">
            {t("settingsPage.security.currentTimer", {
              minutes: userProfile.transactionTimer,
              hours: Math.floor(userProfile.transactionTimer / 60),
              mins: userProfile.transactionTimer % 60,
            })}
          </p>
        )}
        <p className="text-xs text-gray-500">{t("settingsPage.security.timerRange")}</p>
      </div>

      {/* Deposit Fee Payer */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px]" ref={depositFeeRef}>
        <div className="mb-3">
          <h3 className="font-medium text-gray-900 text-base sm:text-lg">{t("settingsPage.security.depositFeePayer")}</h3>
          <p className="text-sm text-gray-600">{t("settingsPage.security.depositFeePayerDesc")}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-3">
          <div className="relative flex-1">
            <button onClick={() => setDepositFeeDropdownOpen(!depositFeeDropdownOpen)} disabled={profileLoading} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed">
              <span className="text-sm text-gray-900">{userProfile.depositFeePayer === "user" ? t("settingsPage.security.userPaysFee") : t("settingsPage.security.merchantPaysFee")}</span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${depositFeeDropdownOpen ? "transform rotate-180" : ""}`} />
            </button>
            {depositFeeDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                <button onClick={() => { handleFeePayerUpdate("deposit", "user"); setDepositFeeDropdownOpen(false); }} disabled={profileLoading} className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors first:rounded-t-md ${userProfile.depositFeePayer === "user" ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-900"} disabled:opacity-50 disabled:cursor-not-allowed`}>
                  <div className="font-medium">{t("settingsPage.security.userPaysFee")}</div>
                  <div className="text-xs text-gray-500 mt-1">{t("settingsPage.security.feeDeductedFromDeposit")}</div>
                </button>
                <button onClick={() => { handleFeePayerUpdate("deposit", "merchant"); setDepositFeeDropdownOpen(false); }} disabled={profileLoading} className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors last:rounded-b-md ${userProfile.depositFeePayer === "merchant" ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-900"} disabled:opacity-50 disabled:cursor-not-allowed`}>
                  <div className="font-medium">{t("settingsPage.security.merchantPaysFee")}</div>
                  <div className="text-xs text-gray-500 mt-1">{t("settingsPage.security.fullAmountCredited")}</div>
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center sm:w-auto">
            <span className="text-sm font-medium text-gray-700">
              {t("settingsPage.security.current")}: <span className="text-blue-600 capitalize">{userProfile.depositFeePayer}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  //  TAB 4: WALLETS
  // ══════════════════════════════════════════════════════════════

  const renderWalletsTab = () => (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
          {t("settingsPage.tabs.wallets.name")}
        </h2>
        <p className="text-sm text-gray-600 mb-6">{t("settingsPage.tabs.wallets.description")}</p>
      </div>

      {/* Static Wallets */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px] space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 text-base sm:text-lg">{t("settingsPage.tabs.wallets.staticWallets")}</h3>
            <p className="text-sm text-gray-600">{t("settingsPage.tabs.wallets.staticWalletsDesc")}</p>
          </div>
          <button onClick={() => setStaticWallets((prev) => ({ ...prev, isEnabled: !prev.isEnabled }))} disabled={staticWalletLoading} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${staticWallets.isEnabled ? "bg-blue-600" : "bg-gray-200"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${staticWallets.isEnabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        {staticWalletError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{staticWalletError}</p>
          </div>
        )}
        {staticWalletSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{staticWalletSuccess}</p>
          </div>
        )}

        {/* ERC20 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">ERC20 Address</label>
            <button onClick={() => setStaticWallets((prev) => ({ ...prev, erc20: { ...prev.erc20, enabled: !prev.erc20.enabled } }))} disabled={!staticWallets.isEnabled || staticWalletLoading} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${staticWallets.erc20.enabled ? "bg-blue-600" : "bg-gray-200"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${staticWallets.erc20.enabled ? "translate-x-5" : "translate-x-1"}`} />
            </button>
          </div>
          <input type="text" value={staticWallets.erc20.address} onChange={(e) => setStaticWallets((prev) => ({ ...prev, erc20: { ...prev.erc20, address: e.target.value } }))} placeholder="0x..." disabled={!staticWallets.isEnabled} className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-60" />
          <p className="text-xs text-gray-500">Used when ERC20 is enabled.</p>
          {staticWallets.erc20.history?.length > 0 && (
            <div className="mt-2">
              <button onClick={() => setShowHistory((prev) => ({ ...prev, erc20: !prev.erc20 }))} className="text-blue-600 text-xs hover:underline">
                {showHistory.erc20 ? "Hide" : "Show"} Address History ({staticWallets.erc20.history.length})
              </button>
              {showHistory.erc20 && (
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {staticWallets.erc20.history.map((item, index) => (
                    <div key={index} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      <div className="font-mono break-all">{item.address}</div>
                      <div className="text-gray-500 mt-1">Changed on {formatDate(item.updatedAt)} by {item.updatedByRole}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* TRC20 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">TRC20 Address</label>
            <button onClick={() => setStaticWallets((prev) => ({ ...prev, trc20: { ...prev.trc20, enabled: !prev.trc20.enabled } }))} disabled={!staticWallets.isEnabled || staticWalletLoading} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${staticWallets.trc20.enabled ? "bg-blue-600" : "bg-gray-200"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${staticWallets.trc20.enabled ? "translate-x-5" : "translate-x-1"}`} />
            </button>
          </div>
          <input type="text" value={staticWallets.trc20.address} onChange={(e) => setStaticWallets((prev) => ({ ...prev, trc20: { ...prev.trc20, address: e.target.value } }))} placeholder="T..." disabled={!staticWallets.isEnabled} className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-60" />
          <p className="text-xs text-gray-500">Used when TRC20 is enabled.</p>
          {staticWallets.trc20.history?.length > 0 && (
            <div className="mt-2">
              <button onClick={() => setShowHistory((prev) => ({ ...prev, trc20: !prev.trc20 }))} className="text-blue-600 text-xs hover:underline">
                {showHistory.trc20 ? "Hide" : "Show"} Address History ({staticWallets.trc20.history.length})
              </button>
              {showHistory.trc20 && (
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {staticWallets.trc20.history.map((item, index) => (
                    <div key={index} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      <div className="font-mono break-all">{item.address}</div>
                      <div className="text-gray-500 mt-1">Changed on {formatDate(item.updatedAt)} by {item.updatedByRole}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 2FA Code Input */}
        {staticAwaitingCode && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Enter verification code</label>
            <input type="text" value={staticCode} maxLength={6} onChange={(e) => { setStaticCode(e.target.value); setStaticCodeError(""); }} className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="6-digit code" disabled={staticWalletLoading} />
            {staticCodeError && <p className="text-xs text-red-600">{staticCodeError}</p>}
            <p className="text-xs text-gray-500">A code was sent to verify this change.</p>
          </div>
        )}

        {/* Save / Reset Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={handleSubmitStaticWallet} disabled={staticWalletLoading} className="flex-1 px-5 py-2.5 bg-gradient-to-b from-[#3A5AD1] to-[#31ABFF] text-white rounded-lg hover:opacity-90 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {staticWalletLoading ? "Saving..." : staticAwaitingCode ? "Submit Code" : "Save Static Wallets"}
          </button>
          <button onClick={() => { setStaticWallets(defaultStaticWallets); setStaticWalletError(""); setStaticWalletSuccess(""); setStaticAwaitingCode(false); setStaticCode(""); }} disabled={staticWalletLoading} className="flex-1 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            Reset
          </button>
        </div>
      </div>

      {/* Threshold Wallet Limit */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px] space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 text-base sm:text-lg">{t("settingsPage.tabs.wallets.thresholdTitle")}</h4>
            <p className="text-sm text-gray-600">{t("settingsPage.tabs.wallets.thresholdDesc")}</p>
          </div>
          <button onClick={() => setThresholdSettings((prev) => ({ ...prev, enabled: !prev.enabled }))} disabled={thresholdLoading} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${thresholdSettings.enabled ? "bg-blue-600" : "bg-gray-200"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${thresholdSettings.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        {(thresholdError || thresholdSuccess) && (
          <div className={`p-3 rounded-lg ${thresholdError ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
            {thresholdError || thresholdSuccess}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t("settingsPage.tabs.wallets.thresholdAmount")}</label>
            <input type="number" value={thresholdSettings.amount} onChange={(e) => setThresholdSettings((prev) => ({ ...prev, amount: e.target.value }))} placeholder="1000" min="0" step="0.01" disabled={thresholdLoading} className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-60" />
            <p className="text-xs text-gray-500">{t("settingsPage.tabs.wallets.thresholdAmountDesc")}</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t("settingsPage.tabs.wallets.currency")}</label>
            <select value={thresholdSettings.currency} onChange={(e) => setThresholdSettings((prev) => ({ ...prev, currency: e.target.value }))} disabled={thresholdLoading} className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-60">
              <option value="USDT">USDT</option>
            </select>
          </div>
        </div>

        {thresholdAwaitingCode && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Enter verification code</label>
            <input type="text" value={thresholdCode} maxLength={6} onChange={(e) => { setThresholdCode(e.target.value); setThresholdCodeError(""); }} className="w-full px-3 py-2 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="6-digit code" disabled={thresholdLoading} />
            {thresholdCodeError && <p className="text-xs text-red-600">{thresholdCodeError}</p>}
            <p className="text-xs text-gray-500">A code was sent to verify this change.</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={handleSubmitThresholdSettings} disabled={thresholdLoading} className="flex-1 px-5 py-2.5 bg-gradient-to-b from-[#3A5AD1] to-[#31ABFF] text-white rounded-lg hover:opacity-90 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {thresholdLoading ? "Saving..." : thresholdAwaitingCode ? "Submit Code" : "Save Threshold Settings"}
          </button>
          <button onClick={() => { setThresholdSettings({ enabled: false, amount: "", currency: "USDT" }); setThresholdError(""); setThresholdSuccess(""); setThresholdAwaitingCode(false); setThresholdCode(""); }} disabled={thresholdLoading} className="flex-1 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            Reset
          </button>
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  //  TAB 5: API & WEBHOOKS
  // ══════════════════════════════════════════════════════════════

  const renderApiTab = () => (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
          {t("settingsPage.tabs.api.name")}
        </h2>
        <p className="text-sm text-gray-600 mb-6">{t("settingsPage.tabs.api.description")}</p>
      </div>

      {/* API Credentials Card */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px] space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-[#348BED1A] rounded-lg p-2">
            <Code className="w-5 h-5 text-[#348BED]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t("apiCredentials.title")}</h3>
            <p className="text-sm text-gray-600">{t("apiCredentials.useCredentials")}</p>
          </div>
        </div>

        {/* User ID */}
        {apiUserId && (
          <div className="p-3 bg-white rounded-[15px]">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">{t("apiCredentials.userId")}</span>
              <span className="text-sm font-mono text-gray-900">{apiUserId}</span>
            </div>
          </div>
        )}

        {apiLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#348BED] border-t-transparent rounded-full animate-spin mb-4"></div>
            <span className="text-sm text-gray-600 font-medium">{t("apiCredentials.loadingCredentials")}</span>
          </div>
        ) : apiError ? (
          <div className="bg-red-50 border border-red-200 rounded-[15px] p-4">
            <div className="flex items-start text-red-700">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold block mb-1 text-sm">{t("apiCredentials.errorLoadingCredentials")}</span>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("apiCredentials.apiKey")}</label>
              <div className="flex items-stretch gap-0">
                <div className="flex-1 p-3 bg-white border border-gray-200 rounded-l-[15px] font-mono text-xs sm:text-sm break-all overflow-x-auto">
                  {canViewApiCredentials ? apiKey || "YOUR_API_KEY_HERE" : "***********************************"}
                </div>
                <button onClick={() => copyToClipboard(apiKey, "key")} className={`${canManageApiCredentials ? "bg-[#348BED] hover:bg-[#2d7bd8] text-white border-[#348BED]" : "bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300"} px-4 py-3 rounded-r-[15px] transition-colors border border-l-0 flex items-center justify-center`} disabled={!apiKey || !canManageApiCredentials}>
                  {copiedKey ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* API Secret */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("apiCredentials.apiSecret")}</label>
              <div className="flex items-stretch gap-0">
                <div className="flex-1 p-3 bg-white border border-gray-200 rounded-l-[15px] font-mono text-xs sm:text-sm break-all max-h-32 overflow-y-auto">
                  {!canViewApiCredentials ? "***********************************" : showApiSecret ? apiSecret || "YOUR_API_SECRET_HERE" : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                </div>
                <button onClick={() => setShowApiSecret(!showApiSecret)} className={`${canViewApiCredentials ? "bg-gray-600 hover:bg-gray-700 text-white border-gray-600" : "bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300"} px-4 py-3 transition-colors border border-l-0 flex items-center justify-center`} disabled={!canViewApiCredentials}>
                  {!showApiSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                <button onClick={() => copyToClipboard(apiSecret, "secret")} className={`${canManageApiCredentials ? "bg-[#348BED] hover:bg-[#2d7bd8] text-white border-[#348BED]" : "bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300"} px-4 py-3 rounded-r-[15px] transition-colors border border-l-0 flex items-center justify-center`} disabled={!apiSecret || !canManageApiCredentials}>
                  {copiedSecret ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Webhook Secret */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">{t("apiCredentials.webhookSecret")}</label>
                {canManageApiCredentials && (
                  <button onClick={() => { webhookSecret ? setShowRotateConfirm(true) : handleRotateWebhookSecret(); }} disabled={rotateLoading} className="flex items-center gap-1.5 text-xs font-medium text-[#348BED] hover:text-[#2d7bd8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <RefreshCw className={`w-3.5 h-3.5 ${rotateLoading ? "animate-spin" : ""}`} />
                    {webhookSecret ? t("apiCredentials.rotateSecret") : t("apiCredentials.generateSecret")}
                  </button>
                )}
              </div>

              {webhookLoading ? (
                <div className="p-3 bg-white border border-gray-200 rounded-[15px]">
                  <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ) : webhookSecret === null && !webhookSecretFull ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-[15px]">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-700">{t("apiCredentials.noWebhookSecret")}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-stretch gap-0">
                    <div className="flex-1 p-3 bg-white border border-gray-200 rounded-l-[15px] font-mono text-xs sm:text-sm break-all max-h-32 overflow-y-auto">
                      {!canViewApiCredentials ? "***********************************" : showWebhookSecret ? webhookSecretFull || webhookSecret || "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : webhookSecret || "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                    </div>
                    <button onClick={() => setShowWebhookSecret(!showWebhookSecret)} className={`${canViewApiCredentials && webhookSecretFull ? "bg-gray-600 hover:bg-gray-700 text-white border-gray-600" : "bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300"} px-4 py-3 transition-colors border border-l-0 flex items-center justify-center`} disabled={!canViewApiCredentials || !webhookSecretFull}>
                      {!showWebhookSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                    <button onClick={() => copyToClipboard(webhookSecretFull || webhookSecret, "webhook")} className={`${canManageApiCredentials && (webhookSecretFull || webhookSecret) ? "bg-[#348BED] hover:bg-[#2d7bd8] text-white border-[#348BED]" : "bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300"} px-4 py-3 rounded-r-[15px] transition-colors border border-l-0 flex items-center justify-center`} disabled={!canManageApiCredentials || (!webhookSecretFull && !webhookSecret)}>
                      {copiedWebhook ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                  {webhookSecretFull && (
                    <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-[10px]">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-green-700">{t("apiCredentials.webhookSecretRotated")}</p>
                      </div>
                    </div>
                  )}
                  {!webhookSecretFull && webhookSecret && (
                    <p className="text-xs text-gray-500 mt-1.5">{t("apiCredentials.webhookSecretMaskedHint")}</p>
                  )}
                </>
              )}

              {webhookError && (
                <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-[10px]">
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {webhookError}
                  </p>
                </div>
              )}
            </div>

            {/* Security Warning */}
            <div className="bg-red-50 border border-red-200 rounded-[15px] p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-red-800 block mb-1 text-sm">{t("apiCredentials.securityWarning")}</span>
                  <p className="text-red-700 text-xs">{t("apiCredentials.securityMessage")}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rotate Confirmation Modal */}
      {showRotateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[20px] p-6 max-w-sm mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-100 rounded-full p-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t("apiCredentials.rotateConfirmTitle")}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{t("apiCredentials.rotateConfirmMessage")}</p>
            <div className="flex gap-3">
              <button onClick={() => setShowRotateConfirm(false)} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-[10px] text-sm font-medium transition-colors">{t("common.cancel")}</button>
              <button onClick={handleRotateWebhookSecret} disabled={rotateLoading} className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-[10px] text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {rotateLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
                {t("apiCredentials.rotateSecret")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Best Practices */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px]">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t("apiCredentials.bestPractices")}</h3>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((num) => (
            <div key={num} className="flex items-start gap-3">
              <div className="bg-[#348BED] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">{num}</div>
              <p className="text-gray-700 text-sm leading-relaxed">{t(`apiCredentials.practice${num}`)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Documentation Link */}
      <div className="p-4 bg-[#F6F7F8] rounded-[15px]">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-[#348BED1A] rounded-lg p-2">
            <ExternalLink className="w-5 h-5 text-[#348BED]" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">{t("apiCredentials.documentation")}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">{t("apiCredentials.documentationDescription")}</p>
        <a href="https://docs.3pa-y.com/docs/getting-started" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-[10px] text-gray-700 font-medium transition-colors text-sm">
          <span>{t("apiCredentials.viewDocumentation")}</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  //  MAIN RENDER
  // ══════════════════════════════════════════════════════════════

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return renderProfileTab();
      case "security":
        return renderSecurityTab();
      case "payment":
        return renderPaymentConfigTab();
      case "wallets":
        return renderWalletsTab();
      case "api":
        return renderApiTab();
      default:
        return renderProfileTab();
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back to Dashboard */}
        <button onClick={() => navigate("/New-Dashboard")} className="flex items-center gap-2 mb-4 md:mb-6 text-sm md:text-base font-semibold text-gray-700 hover:text-gray-900 transition-colors bg-white border border-gray-200 rounded-xl px-3 md:px-4 py-2 shadow-sm">
          <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
          <span>{t("settingsPage.backToDashboard")}</span>
        </button>

        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t("settingsPage.title")}</h1>
          <p className="text-sm text-gray-600">{t("settingsPage.subtitle")}</p>
        </div>

        {/* Main Container */}
        <div className="flex flex-col lg:flex-row gap-2 lg:gap-0 items-stretch">
          {/* Sidebar Navigation */}
          <div className="bg-white p-3 md:p-4 rounded-[30px] lg:rounded-tr-none lg:rounded-br-none">
            <div className="w-full lg:w-[250px] flex-shrink-0 bg-[#F6F7F8] rounded-[20px] shadow-sm p-1 md:p-3">
              <nav className="space-y-0.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2 md:py-3 rounded-[10px] text-left transition-all ${activeTab === tab.id ? "bg-white" : "hover:bg-gray-50"}`}
                    >
                      <div className="flex-shrink-0">
                        <Icon className={`w-4 h-4 md:w-5 md:h-5 ${activeTab === tab.id ? "text-blue-600" : "text-gray-500"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs md:text-sm font-medium ${activeTab === tab.id ? "text-gray-900" : "text-gray-700"}`}>
                          {tab.name}
                        </div>
                        <div className="hidden md:block text-xs text-gray-500 leading-tight mt-0.5">
                          {tab.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white shadow-sm p-4 md:p-6 lg:p-8 min-h-[400px] md:min-h-[500px] rounded-[30px] lg:rounded-tl-none lg:rounded-bl-none">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
